
import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Users, ShieldCheck, Mail, Info } from 'lucide-react';

const ROISimulator: React.FC = () => {
  const [agents, setAgents] = useState(2);
  const hourlyRate = 10;
  const dailyRate = 80;

  const packageInfo = useMemo(() => {
    if (agents >= 20) return { name: 'Elite', price: 'Sur mesure', perAgent: null, color: 'text-purple-600', bg: 'bg-purple-50' };
    if (agents >= 5) return { name: 'Pro', price: agents * 900, perAgent: 900, color: 'text-copper-600', bg: 'bg-copper-50' };
    if (agents >= 2) return { name: 'Essentiel', price: agents * 950, perAgent: 950, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    return { name: 'Individuel', price: agents * 1200, perAgent: 1200, color: 'text-slate-600', bg: 'bg-slate-50' };
  }, [agents]);

  const monthlyCost = packageInfo.perAgent ? packageInfo.price : 0;
  const standardMarketCost = agents * 3500; // Estimated FR/CA cost (salary + charges + infra)
  const savings = standardMarketCost - (typeof monthlyCost === 'number' ? monthlyCost : 0);

  return (
    <div className="bg-white border border-sand-300 rounded-[2.5rem] p-6 lg:p-12 shadow-2xl shadow-sand-300/50 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-copper-100/30 blur-3xl -z-10 rounded-full"></div>
      
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center space-x-2 text-copper-600 font-bold mb-6 text-sm tracking-widest uppercase">
            <Calculator className="w-5 h-5" />
            <span>Simulateur Stratégique</span>
          </div>
          <h2 className="text-4xl font-bold mb-8 text-slate-900 font-heading leading-tight">Estimez votre rentabilité.</h2>
          
          <div className="space-y-10">
            <div className="space-y-6">
              <div className="flex justify-between items-end">
                <label className="text-lg font-semibold text-slate-700">Taille de l'équipe dédiée</label>
                <div className="text-right">
                   <span className="text-3xl font-bold text-copper-600 font-heading">{agents}</span>
                   <span className="text-slate-500 ml-1 font-medium">Talents</span>
                </div>
              </div>
              <input 
                type="range" min="1" max="50" value={agents} 
                onChange={(e) => setAgents(parseInt(e.target.value))}
                className="w-full h-3 bg-sand-200 rounded-lg appearance-none cursor-pointer accent-copper-600"
              />
              <div className="flex justify-between text-xs text-slate-400 font-bold uppercase tracking-tighter">
                <span>1 Agent</span>
                <span>Elite (20+)</span>
              </div>
            </div>

            <div className={`p-6 rounded-2xl border ${packageInfo.bg} ${packageInfo.color.replace('text', 'border')} transition-all`}>
               <div className="flex justify-between items-center mb-2">
                  <h4 className="font-bold uppercase tracking-wider text-sm">Formule {packageInfo.name}</h4>
                  <ShieldCheck className="w-5 h-5" />
               </div>
               <p className="text-slate-600 text-sm leading-relaxed">
                  {agents >= 5 ? "Inclus : Un superviseur dédié et un responsable qualité pour garantir l'excellence." : "Support de base avec agent expérimenté PrimeConnect."}
               </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div className="p-4 bg-sand-100 rounded-xl border border-sand-200">
                  <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Coût Horaire / Agent</p>
                  <p className="text-xl font-bold text-slate-900 font-heading">{hourlyRate}€ / h</p>
               </div>
               <div className="p-4 bg-sand-100 rounded-xl border border-sand-200">
                  <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Forfait Journalier</p>
                  <p className="text-xl font-bold text-slate-900 font-heading">{dailyRate}€ / j</p>
               </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-[2rem] p-10 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-network-dots opacity-10"></div>
          
          <div className="relative z-10">
            <div className="mb-10 text-center">
              <p className="text-sand-300/60 text-xs font-bold uppercase tracking-widest mb-2">Investissement Mensuel</p>
              <div className="text-6xl font-bold font-heading mb-2">
                {typeof monthlyCost === 'number' ? `${monthlyCost.toLocaleString()}€` : packageInfo.price}
                {packageInfo.perAgent && <span className="text-xl font-normal text-sand-300/40 ml-2">HT/mois</span>}
              </div>
              <p className="text-sand-300/50 text-sm italic">Soit {packageInfo.perAgent ? `${packageInfo.perAgent}€ par agent` : 'tarif sur mesure'}</p>
            </div>
            
            <div className="h-px bg-white/10 my-10"></div>
            
            <div className="mb-10 text-center">
              <p className="text-emerald-400 font-bold text-xs mb-3 uppercase tracking-widest flex items-center justify-center">
                <TrendingUp className="w-4 h-4 mr-2" /> Gain moyen constaté
              </p>
              <div className="text-4xl font-bold font-heading text-emerald-400">
                + {agents >= 20 ? '55' : '45'}% <span className="text-lg font-normal text-sand-300/60">d'économie vs interne</span>
              </div>
            </div>

            <button className="w-full bg-copper-600 hover:bg-copper-700 py-5 rounded-2xl font-bold text-white transition-all transform hover:scale-[1.02] shadow-xl shadow-copper-600/20 flex items-center justify-center space-x-2">
              <Mail className="w-5 h-5" />
              <span>Recevoir l'offre détaillée</span>
            </button>
            
            <div className="mt-8 flex items-start space-x-3 text-white/40 text-xs italic">
               <Info className="w-4 h-4 shrink-0" />
               <p>Simulation indicative incluant frais de gestion, infrastructure technologique et formation continue des agents.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROISimulator;
